import React from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow, Paper, Box, TableContainer, Button, Select, MenuItem } from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp'
import DraftsIcon from '@material-ui/icons/Drafts'
import { SortOrder } from './constants'
import Download from '../../../elements/icons/Download'

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]
const DEFAULT_ROW_HEIGHT = 52

const getPageNumbers = (currentPage, totalPages) => {
  const pages = []
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i)
    return pages
  }
  pages.push(0)
  if (currentPage > 2) pages.push('...')
  const start = Math.max(1, currentPage - 1)
  const end = Math.min(totalPages - 2, currentPage + 1)
  for (let i = start; i <= end; i++) pages.push(i)
  if (currentPage < totalPages - 3) pages.push('...')
  pages.push(totalPages - 1)
  return pages
}

const TableData = ({
  classes,
  tableHeadings,
  tableContents,
  isBold = '',
  handleSortChange = () => { },
  renderCellContent,
  getCellClassName = () => '',
  expandedRows = {},
  expandedIntervals = {},
  buildEventLogs = () => null,
  buildEventDetails = () => null,
  buildIntervals = () => null,
  renderExpandedRow,
  renderInvoiceExpandedRow,
  containerClassName,
  tableClassName,
  stickyHeader = true,
  exportIntervalsCsv = () => null,
  emptyTableText = 'No Data Found',
  paginate = false,
  page = 0,
  rowsPerPage = 25,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const totalRows = (tableContents || []).length
  const totalPages = paginate ? Math.max(1, Math.ceil(totalRows / rowsPerPage)) : 1
  const safePage = Math.min(page, totalPages - 1)
  const displayContents = paginate
    ? (tableContents || []).slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage)
    : tableContents
  const emptyRows = paginate ? Math.max(0, rowsPerPage - ((displayContents || []).length)) : 0

  const handleRowsPerPageChange = (e) => {
    if (onRowsPerPageChange) onRowsPerPageChange(e.target.value)
    if (onPageChange) onPageChange(0)
  }

  return (
    <Box className={classes.container}>
      <TableContainer className={containerClassName || classes.tableWrap} component={Paper}>
        <Table stickyHeader={stickyHeader} className={tableClassName || classes.eventsTable}>
          <TableHead>
            <TableRow>
              {tableHeadings.map((heading) => (
                <TableCell key={heading.id} className={classes.tableHeader}>
                  <Box display="flex" alignItems="center">
                    <Box component="span" className={heading.id === isBold ? classes.isBold : ''}>
                      {heading.label}
                    </Box>
                    {heading.sortOrder !== null && handleSortChange && (
                      <Box className={classes.sortIcon}>
                        {heading.id === isBold ? (
                          <Box className={classes.sortContainer}>
                            <ArrowDropUpIcon
                              className={`${classes.sortUp} ${heading.sortOrder === SortOrder.ASC ? classes.active : classes.inactive
                                }`}
                              fontSize="small"
                              onClick={() => handleSortChange(heading.id, SortOrder.ASC)}
                            />
                            <ArrowDropDownIcon
                              className={`${classes.sortDown} ${heading.sortOrder === SortOrder.DESC ? classes.active : classes.inactive
                                }`}
                              fontSize="small"
                              onClick={() => handleSortChange(heading.id, SortOrder.DESC)}
                            />
                          </Box>
                        ) : (
                          <Box className={classes.sortContainer}>
                            <ArrowDropUpIcon
                              className={`${classes.sortUp} ${classes.inactive}`}
                              fontSize="small"
                              onClick={() => handleSortChange(heading.id, SortOrder.ASC)}
                            />
                            <ArrowDropDownIcon
                              className={`${classes.sortDown} ${classes.inactive}`}
                              fontSize="small"
                              onClick={() => handleSortChange(heading.id, SortOrder.DESC)}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayContents && displayContents.length > 0 ? (
              <>
                {displayContents.map((item, index) => (
                  <React.Fragment key={item.id || index}>
                    <TableRow>
                      {tableHeadings.map((heading) => (
                        <TableCell key={heading.id} className={getCellClassName(item, heading, index) || classes.titleWrapper}>
                          {renderCellContent(item, heading, index)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {expandedRows[index] && buildEventLogs && buildEventDetails && (
                      <TableRow className={classes.expandRow}>
                        <TableCell colSpan={tableHeadings.length}>
                          <Box className={classes.expandInner}>
                            <Box className={classes.sectionTitle}>
                              Event Log Timeline
                            </Box>
                            {buildEventLogs(item)}
                            <Box className={classes.eventDetailsSection}>
                              <Box className={classes.sectionTitle}>
                                Event Details
                              </Box>
                              {buildEventDetails(item)}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {expandedIntervals[index] && buildIntervals && (
                      <TableRow className={classes.expandRow}>
                        <TableCell colSpan={tableHeadings.length}>
                          <Box className={classes.expandInner}>
                            <Box className={classes.intervalsHeader}>
                              <Box className={classes.sectionTitle} mb={0}>
                                Intervals — {item.name}
                              </Box>
                              {exportIntervalsCsv && (
                                <Button className={classes.exportBtn} onClick={() => exportIntervalsCsv(item)} size="small">
                                  <Download />
                                  <Box paddingLeft='10px'>Export CSV</Box>
                                </Button>
                              )}
                            </Box>
                            {buildIntervals(item)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {renderExpandedRow && renderExpandedRow(item, index, tableHeadings.length)}
                    {renderInvoiceExpandedRow && renderInvoiceExpandedRow(item, index, tableHeadings.length) && (
                      <TableRow className={classes.expandRow}>
                        <TableCell colSpan={tableHeadings.length}>
                          {renderInvoiceExpandedRow(item, index, tableHeadings.length)}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {emptyRows > 0 && safePage < totalPages - 1 && (
                  <TableRow style={{ height: DEFAULT_ROW_HEIGHT * emptyRows }}>
                    <TableCell colSpan={tableHeadings.length} />
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeadings.length}>
                  <Box className={classes.emptyTableContainer}>
                    <DraftsIcon fontSize="large" color="disabled" />
                    {emptyTableText}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginate && (
        <Box className={classes.paginationBar}>
          <Box className={classes.paginationLeft}>
            <Box component="span" className={classes.paginationLabel}>Rows per page</Box>
            <Select
              className={classes.paginationSelect}
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              disableUnderline
              MenuProps={{
                classes: { paper: classes.selectPaper },
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
                getContentAnchorEl: null,
              }}
            >
              {ROWS_PER_PAGE_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
            <Box component="span" className={classes.paginationInfo}>
              {safePage * rowsPerPage + 1}-{Math.min((safePage + 1) * rowsPerPage, totalRows)} of {totalRows}
            </Box>
          </Box>
          {totalPages > 1 && (
            <Box className={classes.paginationRight}>
              <Button
                className={classes.paginationArrow}
                disabled={safePage === 0}
                onClick={() => onPageChange(safePage - 1)}
              >
                ‹
              </Button>
              {getPageNumbers(safePage, totalPages).map((p, idx) =>
                p === '...' ? (
                  <Box key={`ellipsis-${idx}`} className={classes.paginationEllipsis}>…</Box>
                ) : (
                  <Button
                    key={p}
                    className={`${classes.paginationPageBtn} ${p === safePage ? classes.paginationPageBtnActive : ''}`}
                    onClick={() => onPageChange(p)}
                  >
                    {p + 1}
                  </Button>
                )
              )}
              <Button
                className={classes.paginationArrow}
                disabled={safePage >= totalPages - 1}
                onClick={() => onPageChange(safePage + 1)}
              >
                ›
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default TableData